import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:bg-ss-neutral-200 disabled:text-ss-neutral-25 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-ss-critical-200/20 dark:aria-invalid:ring-ss-critical-200/40 aria-invalid:border-ss-critical-200 cursor-pointer",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        destructive:
          'bg-ss-critical-200 text-white shadow-xs hover:bg-ss-critical-200/90 focus-visible:ring-ss-critical-200/20 dark:focus-visible:ring-ss-critical-200/40 dark:bg-ss-critical-200/60',
        outline:
          'border bg-transparent border-ss-neutral-200 hover:border-ss-neutral-300  shadow-xs hover:bg-ss-neutral-25 hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        'outline destructive':
          'border bg-transparent border-ss-critical-200 text-ss-critical-200 text-base hover:border-ss-critical-300  shadow-xs hover:bg-ss-critical-25 hover:text-ss-critical-200 dark:bg-input/30 dark:border-ss-critical-200 dark:hover:bg-ss-critical-200/50',
        'outline blue':
          'border bg-transparent border-ss-brand-200 text-ss-brand-200 text-base hover:border-ss-brand-300  shadow-xs hover:bg-ss-brand-25 hover:text-ss-brand-200 dark:bg-input/30 dark:border-ss-brand-200 dark:hover:bg-ss-brand-200/50',

        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 !h-5 !p-0',
        'ghost-gray':
          "text-ss-blue-800 dark:text-ss-neutral-200 font-semibold !h-5 !p-0 [&_svg:not([class*='size-'])]:size-5 shrink-0 [&_svg]:shrink-0 ",
        'ghost-destructive':
          "text-ss-critical-200 dark:text-ss-critical-200 font-semibold !h-5 !p-0 [&_svg:not([class*='size-'])]:size-5 shrink-0 [&_svg]:shrink-0",
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        small: 'h-9 px-3 py-2',
        medium: 'h-10 px-3.5 py-2.5',
        large: 'h-11 px-4 py-2',
        icon: 'size-9',
        'icon-sm': 'size-5 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  disabled,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  // If disabled, set aria-disabled for non-native elements
  const ariaProps = !asChild ? { disabled } : { 'aria-disabled': disabled }

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      tabIndex={disabled ? -1 : 0}
      {...ariaProps}
      {...props}
    />
  )
}

export { Button, buttonVariants }
